import { SelectHTMLAttributes, forwardRef, useState, useRef, useEffect } from 'react';
import { cn } from '../utils/cn';
import type { SelectOption } from '../types';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  searchable?: boolean;
  placeholder?: string;
}

/**
 * Select - Accessible select/dropdown component with optional search
 * 
 * Required CSS classes:
 * - .select-wrapper - Wrapper container
 * - .select-label - Label styles
 * - .select - Base select styles (native)
 * - .select-custom - Custom dropdown container
 * - .select-trigger - Custom dropdown trigger button
 * - .select-dropdown - Dropdown menu container
 * - .select-option - Option item styles
 * - .select-option-selected - Selected option styles
 * - .select-search - Search input styles
 * - .select-error - Error state styles
 * - .select-hint - Hint text styles
 * - .select-error-message - Error message styles
 * 
 * Accessibility:
 * - Keyboard: Tab (focus), Arrow keys (navigate), Enter/Space (select), Escape (close)
 * - Screen reader: Announces label, selected value, and error
 * - Focus: Visible focus indicator, focus trap in dropdown
 * 
 * @example
 * <Select 
 *   label="Country"
 *   options={countries}
 *   value={selectedCountry}
 *   onChange={handleChange}
 * />
 * 
 * @example
 * <Select 
 *   label="Searchable"
 *   options={options}
 *   searchable
 *   placeholder="Search..."
 * />
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      label,
      error,
      hint,
      id,
      options,
      searchable = false,
      placeholder,
      value,
      onChange,
      disabled,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const selectId = id || props.name;
    const selectedOption = options.find((opt) => opt.value === value);

    // Filter options based on search query
    const filteredOptions = searchable
      ? options.filter((opt) =>
          opt.label.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : options;

    // Close dropdown on outside click
    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (e: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(e.target as Node) &&
          triggerRef.current &&
          !triggerRef.current.contains(e.target as Node)
        ) {
          setIsOpen(false);
          setSearchQuery('');
          setFocusedIndex(-1);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Keyboard navigation
    useEffect(() => {
      if (!isOpen) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setFocusedIndex((prev) =>
              prev < filteredOptions.length - 1 ? prev + 1 : prev
            );
            break;
          case 'ArrowUp':
            e.preventDefault();
            setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
            break;
          case 'Enter':
          case ' ':
            e.preventDefault();
            if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
              handleSelect(filteredOptions[focusedIndex].value);
            }
            break;
          case 'Escape':
            e.preventDefault();
            setIsOpen(false);
            triggerRef.current?.focus();
            break;
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, focusedIndex, filteredOptions]);

    // Focus search input when dropdown opens
    useEffect(() => {
      if (isOpen && searchable && searchRef.current) {
        searchRef.current.focus();
      }
    }, [isOpen, searchable]);

    const handleSelect = (selectedValue: string | number) => {
      const syntheticEvent = {
        target: { value: String(selectedValue), name: props.name },
      } as React.ChangeEvent<HTMLSelectElement>;
      onChange?.(syntheticEvent);
      setIsOpen(false);
      setSearchQuery('');
      setFocusedIndex(-1);
    };

    // Use native select for non-searchable, simple cases
    if (!searchable && !disabled) {
      return (
        <div className="select-wrapper">
          {label && (
            <label htmlFor={selectId} className="select-label">
              {label}
            </label>
          )}
          <select
            ref={ref}
            id={selectId}
            className={cn('select', error && 'select-error', className)}
            aria-invalid={!!error}
            aria-describedby={
              error ? `${selectId}-error` : hint ? `${selectId}-hint` : undefined
            }
            value={value}
            onChange={onChange}
            disabled={disabled}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={String(option.value)} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          {error && (
            <p id={`${selectId}-error`} className="select-error-message" role="alert">
              {error}
            </p>
          )}
          {hint && !error && (
            <p id={`${selectId}-hint`} className="select-hint">
              {hint}
            </p>
          )}
        </div>
      );
    }

    // Custom dropdown for searchable or disabled styling
    return (
      <div className="select-wrapper">
        {label && (
          <label htmlFor={selectId} className="select-label">
            {label}
          </label>
        )}
        <div className="select-custom">
          <button
            ref={triggerRef}
            type="button"
            id={selectId}
            className={cn(
              'select-trigger',
              error && 'select-error',
              disabled && 'select-disabled',
              className
            )}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-invalid={!!error}
            aria-describedby={
              error ? `${selectId}-error` : hint ? `${selectId}-hint` : undefined
            }
            disabled={disabled}
            onClick={() => !disabled && setIsOpen(!isOpen)}
          >
            {selectedOption ? selectedOption.label : placeholder || 'Select...'}
          </button>
          {isOpen && (
            <div
              ref={dropdownRef}
              className="select-dropdown"
              role="listbox"
              aria-labelledby={label ? `${selectId}-label` : undefined}
            >
              {searchable && (
                <input
                  ref={searchRef}
                  type="text"
                  className="select-search"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setFocusedIndex(-1);
                  }}
                  aria-label="Search options"
                />
              )}
              <div className="select-options">
                {filteredOptions.length === 0 ? (
                  <div className="select-option select-option-empty">No options found</div>
                ) : (
                  filteredOptions.map((option, index) => (
                    <button
                      key={String(option.value)}
                      type="button"
                      className={cn(
                        'select-option',
                        value === option.value && 'select-option-selected',
                        index === focusedIndex && 'select-option-focused',
                        option.disabled && 'select-option-disabled'
                      )}
                      role="option"
                      aria-selected={value === option.value}
                      disabled={option.disabled}
                      onClick={() => !option.disabled && handleSelect(option.value)}
                      onMouseEnter={() => setFocusedIndex(index)}
                    >
                      {option.label}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        {error && (
          <p id={`${selectId}-error`} className="select-error-message" role="alert">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${selectId}-hint`} className="select-hint">
            {hint}
          </p>
        )}
        {/* Hidden native select for form submission */}
        <select
          ref={ref}
          name={props.name}
          value={value}
          onChange={onChange}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
        >
          {options.map((option) => (
            <option key={String(option.value)} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }
);

Select.displayName = 'Select';
